import { externalReadingFormats, getAvailableSettingNumber } from './info_utils';
import { GetUnit } from '../Constants/constants';
import { getLightProfileColour } from './light_profile_colours';

export const irrigationPhaseColors = [0, 1, 2, 3].map((phase) => getLightProfileColour('0:' + phase));

// GUI availability only; account/controller access remains enforced by the server.
export const supportsDatabaseHistory = (controller, hostname = window.location.hostname, previewEnabled = false) =>
  (previewEnabled || ['localhost', '127.0.0.1', '[::1]', '::1'].includes(hostname)) &&
  [5052, 5053, 5055, 5155].includes(controller?.type);

// Presentation of existing database receipts. No reconstructed snapshots or
// inferred changes: every value stays attached to its own receipt and zone.
export const databaseCommands = [
  '',
  'update_values',
  'measurement_batch',
  'message',
  'settings_request',
  'update_alarms',
  'resp_update_setpoints'
];

// Presentation only: retain full precision in stored samples and calculations.
export const formatDatabaseNumber = (value, digits = 2) =>
  value == null ? '—' : value.toFixed(Math.min(digits, 2));

// New receipts carry a comparison captured by the server before dispatch.
// Legacy receipts only describe submitted values; never compare to today's GUI state.
export function databaseSettingsFields(payload) {
  const fields = [];
  const visit = (value, path) => {
    if (value !== null && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length)
      Object.entries(value).forEach(([key, child]) => visit(child, [...path, key]));
    else fields.push({ path });
  };
  if ([1, 2].includes(payload.request_summary?.version)) fields.push(...payload.request_summary.fields);
  else Object.entries(payload.data ?? {}).forEach(([key, value]) => {
    if (!['id', 'name', 'zone_id', 'timestamp', 'has_zone_data'].includes(key)) visit(value, [key]);
  });
  return fields.map((field) => ({ ...field,
    compared: field.compared ?? Object.prototype.hasOwnProperty.call(field, 'previous'),
    requested: field.path.reduce((value, key) => value?.[key], payload.data) }));
}

export function formatDatabaseSetting(path, value, t) {
  if (value == null) return '—';
  if (typeof value === 'object') return t('database_setting_items', { count: Object.keys(value).length });
  if (typeof value !== 'number') return String(value);
  const [category, field] = path;
  // Climate's editor uses tenths for temperature/VPD and whole percent for RH.
  // Keep VPD in the wire's hPa; current GUI preferences are not historical data.
  const climateTemperature = category === 'climate' && /^temp_sp_(day|night|co2|heater_day|heater_night|heating_mat_(day|night)_(abs|rel))$/.test(field);
  const climateVpd = category === 'climate' && ['vpd_sp', 'vpd_sp_night'].includes(field);
  const climateRh = category === 'climate' && ['humidity_sp_day', 'humidity_sp_night'].includes(field);
  if (climateTemperature || climateVpd || climateRh) {
    const number = getAvailableSettingNumber(value);
    if (number === null) return t('not_available');
    return `${formatDatabaseNumber(number / (climateRh ? 1 : 10), climateRh ? 0 : 1)} ${(GetUnit(undefined, field) ?? '').trim()}`.trim();
  }
  // Unknown setting formats remain in their submitted representation. Do not
  // infer a scale from a name or apply a current controller's changing settings.
  return Number.isInteger(value) ? String(value) : formatDatabaseNumber(value);
}

export function databaseEventKind(row) {
  if (row.payload.command === 'settings_request') return 'settings';
  return row.payload.command === 'update_alarms' || ['warning', 'error'].includes(row.payload.data?.type) ? 'alarms' : 'messages';
}

export const databaseEventZone = (row) => String(
  (row.payload.command === 'settings_request' ? row.payload.data?.id : undefined) ?? row.payload.data?.zone_id ?? '');

export const databaseLocalTime = (time) => {
  const date = new Date(time);
  return new Date(time - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

export function databaseSelectedPeriod(filters) {
  const to = new Date(filters.until).getTime();
  return { from: filters.timeSpan === 'custom' ? new Date(filters.from).getTime() : to - Number(filters.timeSpan) * 3600000, to };
}

function fieldsOf(value, prefix, fields) {
  if (Array.isArray(value)) return; // Array positions are not stable device identities.
  if (value !== null && typeof value === 'object') {
    Object.entries(value).forEach(([key, child]) => fieldsOf(child, `${prefix}.${key}`, fields));
  } else if (value !== null) {
    fields[prefix] = value;
  }
}

export function databaseRecords(rows) {
  const records = [];
  const add = (row, data, zone, name, timestamp) => {
    const categories = Object.entries(data ?? {}).filter(
      ([key]) => !['zones', 'timestamp', 'zone_id'].includes(key)
    );
    const flat = {};
    for (const [category, value] of categories) {
      if (value !== null && typeof value === 'object') {
        const fields = {};
        fieldsOf(value, category, fields);
        records.push({ row, zone, name, timestamp, category, fields });
      } else {
        flat[category] = value;
      }
    }
    if (Object.keys(flat).length || categories.length === 0) {
      records.push({ row, zone, name, timestamp, category: 'data', fields: flat });
    }
  };
  for (const row of rows) {
    const data = row.payload.data ?? {};
    if (row.payload.command === 'measurement_batch') {
      // Summary batches already have a dedicated measurements view. Keep their
      // raw receipt as one expandable row, not hundreds of flattened records.
      records.push({ row, zone: '', name: '', timestamp: undefined, category: 'data', fields: {} });
      continue;
    }
    if (row.payload.command === 'settings_request') {
      // Outgoing zoned settings use a flat id/name envelope, unlike telemetry.
      // Its client timestamp is not a controller-confirmed occurrence time.
      const { id, name, has_zone_data, ...settings } = data;
      add(row, settings, String(id ?? data.zone_id ?? ''), name ?? '', undefined);
      continue;
    }
    // Messages have no reliable zone identity in their human-readable text.
    const zone = data.zone_id == null ? '' : String(data.zone_id);
    const globalData = Object.fromEntries(Object.entries(data).filter(([key]) => key !== 'zones'));
    if (Object.keys(globalData).some((key) => key !== 'timestamp') || !data.zones?.length) {
      add(row, globalData, zone, '', data.timestamp);
    }
    for (const item of data.zones ?? []) {
      add(
        row,
        item.settings,
        String(item.id),
        item.name,
        item.settings?.timestamp ?? data.timestamp
      );
    }
  }
  return records;
}

export function databaseTimeRange(from, until) {
  const start = from ? new Date(from).getTime() : -Infinity;
  // A minute selected without seconds includes that whole minute.
  const end = until ? new Date(until).getTime() + (until.length === 16 ? 59999 : 0) : Infinity;
  return { start, end, invalid: Number.isNaN(start) || Number.isNaN(end) || start > end };
}

// datetime-local inputs display browser-local time. Convert each endpoint with
// its own offset so elapsed-hour presets also work across daylight-saving changes.
export function databaseTimePreset(hours, now = Date.now()) {
  // Period queries have an exclusive end. Include the current minute's readings
  // while keeping the selected duration and minute-resolution controls aligned.
  const until = Math.ceil(now / 60000) * 60000;
  return { from: databaseLocalTime(until - hours * 3600000), until: databaseLocalTime(until) };
}

export function filterDatabaseRecords(records, { zone, category, field, value, from, until }) {
  const range = databaseTimeRange(from, until);
  if (range.invalid) return [];
  return records.filter((record) => {
    if (record.row.received_at < range.start || record.row.received_at > range.end) return false;
    if (zone !== 'all' && record.zone !== zone) return false;
    if (category && record.category !== category) return false;
    if (field && !Object.prototype.hasOwnProperty.call(record.fields, field)) return false;
    return !value || (field && String(record.fields[field]) === value.trim());
  });
}

export function databaseNumber(value) {
  if (typeof value === 'boolean') return value ? 1 : 0;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function databasePoints(records, { field, xAxis, yAxis, groupBy }) {
  const unique = new Map();
  const coordinate = (record, axis) => {
    if (axis === 'received') return databaseNumber(record.row.received_at);
    if (axis === 'source') return databaseNumber(record.timestamp);
    if (axis === 'value') return databaseNumber(record.fields[field]);
    if (axis === 'zone') return record.zone;
    return record.row.payload.command;
  };
  for (const record of records) {
    const x = coordinate(record, xAxis);
    const y = coordinate(record, yAxis);
    if (x === null || y === null) continue;
    const group =
      groupBy === 'zone' ? record.zone : groupBy === 'command' ? record.row.payload.command : '';
    // A packet with several categories is still one receipt per zone on an event plot.
    unique.set(JSON.stringify([record.row.id, record.zone, x, y]), { x, y, group, record });
  }
  return [...unique.values()];
}

// Flappix's contrasting scatter palette; deterministic assignment keeps colours
// stable when filtering or loading another page.
export function databaseSeriesColor(key) {
  const palette = [
    '#dc3232',
    '#14b4b4',
    '#e6641e',
    '#1482d7',
    '#e69b14',
    '#284bdc',
    '#c3c314',
    '#552ddc',
    '#82d214',
    '#8c23d2',
    '#3cd228',
    '#b423c8',
    '#1ec855',
    '#d723af',
    '#19be87',
    '#e1237d',
    '#19b4aa',
    '#e62d50',
    '#19a5c8',
    '#e13c32'
  ];
  let hash = 0;
  for (const char of key) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return palette[hash % palette.length];
}

// Actual stored observations only; never reconstruct values from current state.
export function databaseMeasurements(rows) {
  const rooms = new Map();
  const room = (zone, name = '') => {
    if (!rooms.has(zone)) rooms.set(zone, { id: zone, name, phases: [], activities: new Map(), measurements: new Map() });
    const result = rooms.get(zone);
    if (name) result.name = name;
    return result;
  };
  const measurement = (zone, type, subZone, name = '') => {
    const parent = room(zone, name);
    const id = `${type}:${subZone}`;
    if (!parent.measurements.has(id)) parent.measurements.set(id, {
      id, type, subZone, ...(externalReadingFormats[type] ?? { digits: 2, unit: '', raw: true }),
      series: new Map()
    });
    return parent.measurements.get(id);
  };
  const observation = (metric, id, time, value, props) => {
    if (!metric.series.has(id)) metric.series.set(id, { id, ...props, points: [] });
    const numeric = metric.raw ? databaseNumber(value) : getAvailableSettingNumber(value);
    metric.series.get(id).points.push({
      x: time, y: numeric == null ? null : numeric / (metric.raw ? 1 : 10 ** metric.digits),
      ...(props.excluded !== undefined ? { excluded: props.excluded } : {})
    });
  };
  const uniqueRows = [...new Map(rows.map((row) => [row.id, row])).values()]
    .filter((row) => row.payload.command === 'update_values')
    .sort((a, b) => a.received_at - b.received_at);
  for (const row of uniqueRows) {
    const data = row.payload.data ?? {};
    const seenSensors = new Set();
    const coveredZones = new Set();
    const read = (settings, zone, name, global = false) => {
      room(zone, name);
      if (settings.cur_values) {
        for (const [type, meta] of Object.entries(externalReadingFormats)) {
          if (!meta.mainField) continue;
          for (let subZone = 0; subZone < (meta.mainCount ?? 1); subZone++) {
            const field = meta.mainField + (subZone ? '_' + (subZone + 1) : '');
            const existing = rooms.get(zone).measurements.get(type + ':' + subZone);
            if (!(field in settings.cur_values) && !existing?.series.has('main')) continue;
            observation(measurement(zone, Number(type), subZone, name), 'main', row.received_at,
              settings.cur_values[field], { main: true });
          }
        }
      }
      if (settings.cur_values) {
        const values = settings.cur_values;
        // Missing/unknown phase stays unknown; never reconstruct from today's schedule.
        room(zone).phases.push({ x: row.received_at, y: getAvailableSettingNumber(values.phase) });
        for (let subZone = 0; subZone < 4; subZone++) {
          const field = 'irrigation_state' + (subZone ? '_' + (subZone + 1) : '');
          if (!(field in values) && !room(zone).activities.has(field)) continue;
          const state = getAvailableSettingNumber(values[field]);
          if (!room(zone).activities.has(field)) room(zone).activities.set(field, {
            id: field, label: 'database_shots', subZone, kind: 'shots', points: []
          });
          room(zone).activities.get(field).points.push({
            x: row.received_at, y: state === 4 ? 1 : [0, 1, 2, 3, 5, 6].includes(state) ? 0 : null
          });
        }
      }
      if (Array.isArray(settings.ext_readings?.extrs)) {
        coveredZones.add(global ? '*' : zone);
        for (const ex of settings.ext_readings.extrs) {
          const sensorZone = row.payload.is_zoned === false ? zone : String(ex.zone ?? zone);
          const subZone = Number(ex.sub_zone ?? 0);
          const metric = measurement(sensorZone, Number(ex.type), subZone);
          const id = JSON.stringify([sensorZone, subZone, ex.gcl_addr, ex.port_id, ex.addr_sensor, ex.type]);
          seenSensors.add(id);
          const address = ex.sensor_addr_display_type === 0 && (
            (ex.addr_sensor >= 48 && ex.addr_sensor <= 57) ||
            (ex.addr_sensor >= 65 && ex.addr_sensor <= 90) ||
            (ex.addr_sensor >= 97 && ex.addr_sensor <= 122))
            ? String.fromCharCode(ex.addr_sensor) : String(ex.addr_sensor);
          observation(metric, id, row.received_at, ex.value, {
            main: false, identity: { link: ex.gcl_addr, port: ex.port_id, address },
            excluded: ex.exclude_cur_value === 1 || ex.exclude_cur_value === true
          });
        }
      }
    };
    if (data.cur_values || data.ext_readings) read(data, String(data.zone_id ?? ''), '', true);
    for (const item of data.zones ?? []) read(item.settings ?? {}, String(item.id), item.name);
    // These rows already carry the physical identity and assignment from the
    // serializer. Do not join historical indexes to today's cached settings.
    for (const slave of data.slaves ?? []) {
      for (const port of slave.multi_outs ?? []) {
        if (port.id == null || port.zone == null || ![1, 2].includes(port.type_id)) continue;
        const parent = room(String(port.zone));
        const id = 'output:' + slave.address + ':' + port.id + ':' + port.func + ':' + port.func_index;
        if (!parent.activities.has(id)) parent.activities.set(id, {
          id, kind: 'output', address: slave.address, port: port.id,
          func: port.func, memo: port.memo, points: []
        });
        const state = databaseNumber(port.state);
        parent.activities.get(id).points.push({ x: row.received_at,
          y: state == null || state < 0 ? null : state > 0 ? 1 : 0 });
      }
    }
    if (Array.isArray(data.slaves)) {
      for (const parent of rooms.values()) for (const activity of parent.activities.values()) {
        if (activity.kind === 'output' && activity.points.at(-1)?.x !== row.received_at)
          activity.points.push({ x: row.received_at, y: null });
      }
    }
    // An explicitly absent sensor is a gap, not zero or a carried-forward value.
    // Packets without the reading list say nothing about sensor availability.
    for (const parent of rooms.values()) {
      if (!coveredZones.has('*') && !coveredZones.has(parent.id)) continue;
      for (const metric of parent.measurements.values()) {
        for (const series of metric.series.values()) {
          if (!series.main && !seenSensors.has(series.id)) series.points.push({ x: row.received_at, y: null });
        }
      }
    }
  }
  return [...rooms.values()].map((parent) => ({
    ...parent, activities: [...parent.activities.values()], measurements: [...parent.measurements.values()].map((metric) => ({
      ...metric, series: [...metric.series.values()].sort((a, b) => Number(b.main) - Number(a.main) || a.id.localeCompare(b.id))
    })).sort((a, b) => a.type - b.type || a.subZone - b.subZone)
  })).filter((parent) => parent.measurements.length || parent.activities.length);
}

// Query v1 stores controller-scaled minute summaries; this adapter shares the
// existing reading format catalogue with live values and the receipt view.
export function databaseSummaryMeasurements(response) {
  const rooms = new Map();
  const resolution = response.resolution_ms;
  for (const source of response.series) {
    const zone = String(source.zone);
    if (!rooms.has(zone)) rooms.set(zone, { id: zone, name: '', phases: [], activities: [], measurements: [] });
    const room = rooms.get(zone);
    if (source.name) room.name = source.name;
    if (['phase', 'irrigation_phase', 'shots', 'output', 'status'].includes(source.kind)) {
      const points = [...new Map(source.points.map((point) => [point.x, point])).values()].sort((a, b) => a.x - b.x);
      // A preceding transition is useful only up to a later observation. Never
      // carry a stale state from before the period into an empty period.
      const clipped = points.filter((point, index) => point.x >= response.period.from || points[index + 1]?.x >= response.period.from)
        .map((point) => ({ ...point, x: Math.max(point.x, response.period.from) }));
      if (source.kind === 'phase') room.phases = clipped;
      else room.activities.push({ ...source, points: clipped });
      continue;
    }
    const equipment = source.kind === 'metric';
    const type = equipment ? source.field : source.kind === 'main' ? Number(Object.keys(externalReadingFormats).find((key) => externalReadingFormats[key].mainField === source.field)) : source.type;
    if (!type) continue;
    const subZone = source.subZone ?? source.sub_zone ?? 0;
    const id = equipment ? [type, subZone, source.channel ?? '', source.address ?? '', source.port ?? '', source.func ?? '', source.func_index ?? '', source.tank_id ?? ''].join(':') : type + ':' + subZone;
    let metric = room.measurements.find((item) => item.id === id);
    if (!metric) {
      metric = { id, type, subZone, ...(equipment ? { ...source, id, type, subZone, equipment: true, digits: 2 } :
        externalReadingFormats[type] ?? { digits: 2, unit: '', raw: true }), series: [] };
      room.measurements.push(metric);
    }
    const scale = equipment ? source.scale : metric.raw ? 1 : 1 / 10 ** metric.digits;
    const convert = (value) => value == null ? null : value * scale + (source.offset ?? 0);
    const points = [];
    if (source.discrete) {
      const ordered = [...new Map(source.points.map((point) => [point.x, point])).values()].sort((a, b) => a.x - b.x);
      points.push(...ordered.filter((point, index) => point.x >= response.period.from || ordered[index + 1]?.x >= response.period.from)
        .map((point) => ({ ...point, x: Math.max(point.x, response.period.from), y: convert(point.y) })));
    }
    else source.points.forEach((point, index) => {
      const previous = source.points[index - 1];
      if (previous && point.first_at - previous.last_at > 60000) points.push({ x: Math.max(previous.x + 1, previous.last_at), y: null });
      points.push({ ...point, x: Math.max(point.x, response.period.from),
        y: convert(point.y), min: convert(point.min), max: convert(point.max),
        last: convert(point.last), excluded: point.excluded_count > 0, resolution });
      // A minute with explicit unavailable observations must not join its neighbours.
      if (point.missing || point.gap) points.push({ x: point.x + resolution - 1, y: null });
    });
    const addr = source.addr_sensor;
    const address = source.sensor_addr_display_type === 0 && ((addr >= 48 && addr <= 57) || (addr >= 65 && addr <= 90) || (addr >= 97 && addr <= 122)) ? String.fromCharCode(addr) : String(addr);
    metric.series.push({ id: source.kind === 'main' ? 'main' : source.id, main: equipment || source.kind === 'main', equipment, discrete: source.discrete, points,
      identity: { link: source.gcl_addr, port: source.port_id, address } });
  }
  for (const totals of response.irrigation_totals ?? []) {
    const zone = String(totals.zone);
    if (!rooms.has(zone)) rooms.set(zone, { id: zone, name: '', phases: [], activities: [], measurements: [] });
    const room = rooms.get(zone);
    if (!room.irrigationTotals) room.irrigationTotals = [];
    room.irrigationTotals.push(totals);
  }
  return [...rooms.values()];
}
